import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { X as XIcon } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import styles from './Tag.module.scss';

export type TagVariant = 'fill' | 'outlined';
export type TagColor = 'primary' | 'secondary' | 'accent' | 'success' | 'error' | 'soft';
export type TagSize = 'sm' | 'md' | 'lg';

interface TagBaseProps extends HTMLAttributes<HTMLDivElement> {
    /** @default 'fill' */
    variant?: TagVariant;
    /** Colour role. @default 'primary' */
    color?: TagColor;
    /** @default 'md' */
    size?: TagSize;
    /** Icon before the label. */
    iconLeft?: ReactNode;
}

interface RemovableTagProps extends TagBaseProps {
    /** Shows a × button; called when it's clicked. */
    onRemove: () => void;
    /** Accessible name of the × button, e.g. "Remove filter" (translated). Required with `onRemove`. */
    removeLabel: string;
}

interface StaticTagProps extends TagBaseProps {
    onRemove?: never;
    removeLabel?: never;
}

export type TagProps = RemovableTagProps | StaticTagProps;

/**
 * A short label: a product tag, a status, a filter chip. With `onRemove` it gets a × button.
 *
 * @example
 * <Tag variant="fill" color="soft" size="lg">{tag}</Tag>
 * <Tag onRemove={clearFilter} removeLabel={t('filters.remove')}>{filter}</Tag>
 */
export function Tag({
    variant = 'fill',
    color = 'primary',
    size = 'md',
    iconLeft,
    onRemove,
    removeLabel,
    className,
    children,
    ...props
}: TagProps): ReactElement {
    return (
        <div
            className={classNames(
                styles.tag,
                styles[`variant-${variant}`],
                styles[`color-${color}`],
                styles[`size-${size}`],
                className,
            )}
            {...props}
        >
            {iconLeft && <span className={styles.icon}>{iconLeft}</span>}
            {children && <span className={styles.label}>{children}</span>}
            {onRemove && (
                <button
                    type="button"
                    className={classNames(styles['remove-button'], 'ml-1')}
                    onClick={onRemove}
                    aria-label={removeLabel}
                >
                    <XIcon />
                </button>
            )}
        </div>
    );
}
